import {
  Button,
  Checkbox,
  Col,
  Divider,
  Flex,
  Form,
  Grid,
  Input,
  message,
  Row,
  Switch,
  Typography,
} from "antd";
import style from "./Review.module.css";
import BrandButton from "../../../components/v2/Buttons/BrandButton";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  ACTIONTYPES,
  useStore,
  useStoreDispatch,
} from "../../../context/CreateStoreContext";
import dayjs from "dayjs";
import { businessType } from "../../../constants/typeOfEntity";
import { createStoreV2 } from "../../../components/apiRepository/CreateStoreRepository";
import { useNavigate } from "react-router-dom";
import accountTypes from "../../../constants/accountTypes";
import { pricing2025 as plans } from "../../../constants/pricingTier";
import useComponentSize from "../../../hooks/useComponentSize";
import { debounce } from "lodash";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

export default function Review({ back, handleGotoStep }) {
  const componentSize = useComponentSize();
  const screen = useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const submitButtonRef = useRef();
  const dispatch = useStoreDispatch();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {
    accountType = "OWNER",
    account,
    store,
    contact,
    business,
    operationHours,
    plan,
    stripeClient,
    parentStore,
    integrator,
  } = useStore();
  const [isChild, setIsChild] = useState(accountType === accountTypes.store);

  const onFinish = debounce(async ({ changePayment }) => {
    if (loading) {
      return;
    }

    if (stripeClient?.clientSecret) {
      navigate("/checkout", { replace: true });
      return;
    }

    setLoading(true);

    try {
      const contactDetails = prepareContactDetails(contact);

      const accountDetails = prepareAccountDetails(
        isChild,
        account,
        parentStore,
      );

      const schedules = operationHours.map(({ error, ...rest }) => rest);

      const paymentMethod = determinePaymentMethod(isChild, changePayment);

      const response = await createStoreV2({
        accountType,
        account: accountDetails,
        store,
        contact: contactDetails,
        business: isBusinessEmpty(business) ? undefined : business,
        operationHours: schedules,
        plan,
        eula: true,
        newSignUp: true,
        paymentMethod: isChild ? paymentMethod : undefined,
        integratorId: integrator?.id || undefined,
      });

      handleCreateStoreResponse(response, paymentMethod, isChild);
    } catch (error) {
      console.error("Store creation failed:", error);
      showErrorMessage("Creating Store Failed.");
    } finally {
      setLoading(false);
    }
  }, 500);

  const prepareContactDetails = (contact) => {
    const contactDetails = { ...contact };

    if (!contactDetails?.agreeSecondary) {
      const fieldsToRemove = [
        "secondaryEmailAddress",
        "secondaryFirstName",
        "secondaryLastName",
        "secondaryPhoneNumber",
      ];
      fieldsToRemove.forEach((field) => delete contactDetails[field]);
    }

    delete contactDetails.agreeOwner;
    delete contactDetails.agreeSecondary;

    return contactDetails;
  };

  const prepareAccountDetails = (isChild, account, parentStore) => {
    if (isChild) {
      return {
        username: account?.emailAddress,
        password: account?.password,
        ownerId: parentStore?.ownerId,
      };
    }

    return {
      emailAddress: account?.emailAddress,
      password: account?.password,
    };
  };

  const determinePaymentMethod = (isChild, changePayment) =>
    (isChild && changePayment) || (!isChild && !changePayment)
      ? "NEW"
      : "DEFAULT";

  const isBusinessEmpty = (objBusiness) =>
    Object.values(objBusiness).every((val) => !val || val === "");

  const handleCreateStoreResponse = (
    response,
    paymentMethod,
    isChild,
    isIntegrator,
  ) => {
    if (response?.errors?.length > 0) {
      showErrorMessage(response?.message || "Creating Store Failed.");
      return;
    }

    if (isIntegrator) {
      localStorage.setItem("integratorFormCompleted", 1);
      navigate("/thankyou", { replace: true });
      return;
    }

    const shouldNavigateToCheckout =
      response?.data?.clientSecret && paymentMethod === "NEW";
    const path = shouldNavigateToCheckout
      ? "/checkout"
      : `/thankyou?admin=${isChild}`;
    const delay = shouldNavigateToCheckout ? 1000 : 500;

    if (shouldNavigateToCheckout) {
      dispatch({
        type: ACTIONTYPES.STRIPECLIENT,
        data: { ...response?.data, fromAdmin: isChild },
      });
    }

    setTimeout(() => {
      navigate(path, { replace: true });
    }, delay);
  };

  const showErrorMessage = (content) => {
    messageApi.open({
      type: "error",
      content,
    });
  };

  const handleUpdateDetails = (stepIndex) => {
    handleGotoStep(stepIndex);
  };

  useEffect(() => {
    setIsChild(() => accountType === accountTypes.store);
  }, [accountType]);

  return (
    <>
      {contextHolder}
      <Row className="BevzForm">
        <Col
          xs={{ span: 22, offset: 1 }}
          lg={{ span: 20, offset: 2 }}
          xl={{ span: 15, offset: 2 }}
        >
          <Title
            level={componentSize === "default" ? 4 : 2}
            className="BevzFormHeader"
          >
            Review Summary
          </Title>
          <Paragraph
            className="BevzFormSubHeader"
            style={{
              marginBottom: componentSize === "default" ? "40px" : "1em",
            }}
          >
            Check all the details are correct and accurate.
          </Paragraph>

          <AccountInfo handleUpdateDetails={() => handleUpdateDetails(0)} />
          <Divider
            style={{
              margin: componentSize === "default" ? "30px 0" : "40px 0",
            }}
          />
          <StoreInfo handleUpdateDetails={() => handleUpdateDetails(1)} />
          <Divider style={{ margin: "40px 0" }} />
          <ContactInfo handleUpdateDetails={() => handleUpdateDetails(2)} />
          <Divider style={{ margin: "40px 0" }} />
          <BusinessInfo handleUpdateDetails={() => handleUpdateDetails(3)} />
          <Divider style={{ margin: "40px 0" }} />
          <StoreHours handleUpdateDetails={() => handleUpdateDetails(4)} />
          <Divider style={{ margin: "40px 0" }} />
          <SubsPlan handleUpdateDetails={() => handleUpdateDetails(5)} />
          <Divider style={{ margin: "40px 0" }} />

          <Row>
            <Col>
              <Form form={form} onFinish={onFinish} layout="vertical">
                <Form.Item
                  name="eula"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || value.length !== 1) {
                          return Promise.reject(
                            new Error(
                              "You must confirm your details are accurate and agree to the EULA and Privacy Statement.",
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Checkbox.Group>
                    <Row gutter={[0, 10]}>
                      {/* <Col>
                                    <Checkbox value="option1">I confirm that all the details I provided are correct and accurate.<span style={{ color: "red" }}>*</span></Checkbox>
                                </Col> */}
                      <Col>
                        <Checkbox value="option2" className="BEVZ_Form">
                          By using this application, you agree to the{" "}
                          <a
                            href="https://www.bevz.com/terms-of-service"
                            target="_blank"
                            className={style.link}
                          >
                            End User License Agreement
                          </a>{" "}
                          and the{" "}
                          <a
                            href="https://www.bevz.com/privacy-policy/"
                            target="_blank"
                            className={style.link}
                          >
                            Privacy Statement
                          </a>
                          <span style={{ color: "red" }}>*</span>
                        </Checkbox>
                      </Col>
                    </Row>
                  </Checkbox.Group>
                </Form.Item>

                {isChild ? (
                  <Row style={{ marginBottom: "50px" }}>
                    <Col
                      style={{
                        backgroundColor: "#007aff0d",
                        padding: "20px",
                        borderRadius: "16px",
                      }}
                    >
                      <Paragraph
                        style={{
                          marginBottom: "12px",
                          color: "#007AFF",
                          fontSize: "16px",
                        }}
                      >
                        Your default payment method,{" "}
                        <Text
                          strong
                          style={{
                            color: "inherit",
                            fontSize: "inherit",
                            textTransform: "capitalize",
                          }}
                        >
                          {parentStore?.payment?.brand}
                        </Text>{" "}
                        ending in{" "}
                        <Text
                          strong
                          style={{
                            color: "inherit",
                            fontSize: "inherit",
                          }}
                        >
                          {parentStore?.payment?.last4}
                        </Text>{" "}
                        , will be used to complete the purchase of your{" "}
                        <Text
                          strong
                          style={{
                            color: "inherit",
                            fontSize: "inherit",
                          }}
                        >
                          {plans[plan].label} Plan ${plans[plan].price}/month
                        </Text>
                      </Paragraph>
                      <Paragraph
                        style={{
                          marginBottom: "12px",
                          color: "#939393",
                        }}
                      >
                        To use a different payment method, toggle the switch
                        below.
                      </Paragraph>
                      <Flex
                        style={{
                          marginBottom: 0,
                          border: "1px solid #EAEAEA",
                          padding: "12px 10px",
                          borderRadius: "10px",
                          backgroundColor: "white",
                        }}
                        justify="space-between"
                        align="center"
                      >
                        <Text style={{ color: "#515151" }}>
                          Would you like to change your payment method for this
                          purchase?
                        </Text>{" "}
                        <Form.Item name="changePayment" style={{ margin: 0 }}>
                          <Switch
                            checkedChildren={<CheckOutlined />}
                            unCheckedChildren={<CloseOutlined />}
                            valuePropName="checked"
                          />
                        </Form.Item>
                      </Flex>
                    </Col>
                  </Row>
                ) : null}

                <Row justify="space-between" style={{ marginTop: "30px" }}>
                  <Col
                    xs={{ order: 2, span: 24 }}
                    sm={{ order: 1, span: 10 }}
                    lg={{ span: 12 }}
                  >
                    <Form.Item>
                      <Button
                        type="default"
                        size="large"
                        shape="round"
                        icon={<ArrowLeftOutlined />}
                        onClick={back}
                        block={screen.xs}
                      >
                        Back
                      </Button>
                    </Form.Item>
                  </Col>

                  <Col
                    xs={{ order: 1, span: 24 }}
                    sm={{ order: 2, span: 14 }}
                    lg={{ span: 12 }}
                  >
                    <Form.Item>
                      <BrandButton
                        htmlType="submit"
                        ref={submitButtonRef}
                        loading={loading}
                        size="large"
                        block={true}
                      >
                        {integrator?.id
                          ? "Create Store"
                          : "Create Store and Checkout"}
                      </BrandButton>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}

function AccountInfo({ handleUpdateDetails }) {
  const componentSize = useComponentSize();
  const { account } = useStore();
  const [passwordVisible, setPasswordVisible] = useState(false);

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title
            level={componentSize === "default" ? 5 : 3}
            className={style.SectionTitle}
          >
            Account Information
          </Title>
        </Col>
        <Col>
          <Button type="link" onClick={handleUpdateDetails}>
            Update Details
          </Button>
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Email Address<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={account?.emailAddress}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Password<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input.Password
            style={{ width: "max-content" }}
            visibilityToggle={{
              visible: passwordVisible,
              onVisibleChange: setPasswordVisible,
            }}
            variant="borderless"
            readOnly
            defaultValue={account?.password}
          />
        </Col>
      </Row>
    </div>
  );
}

function StoreInfo({ handleUpdateDetails }) {
  const componentSize = useComponentSize();
  const { store } = useStore();

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title
            level={componentSize === "default" ? 5 : 3}
            className={style.SectionTitle}
          >
            Store Information
          </Title>
        </Col>
        <Col>
          <Button type="link" onClick={handleUpdateDetails}>
            Update Details
          </Button>
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Store Name<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input variant="borderless" readOnly defaultValue={store?.name} />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Legal Corporation Name<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={store?.legalCorporationName}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Store Email Address<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={store?.emailAddress}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Main Store Phone Number<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={store?.phoneNumber}
          />
        </Col>
      </Row>
      <Row align="top" className={style.input_wrapper}>
        <Col span={9}>
          Store Address<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={store?.address?.street1}
          />
          {store?.address?.street2 ? (
            <Input
              variant="borderless"
              readOnly
              defaultValue={store?.address?.street2}
            />
          ) : null}
          <Input
            variant="borderless"
            readOnly
            defaultValue={`${store?.address?.city || ""}, ${
              store?.address?.state || ""
            } ${store?.address?.zipCode || ""}, USA`}
          />
        </Col>
      </Row>
      <Row align="middle">
        <Col span={9}>
          Store Sales Tax %<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={store?.tax ? `${store?.tax}%` : null}
          />
        </Col>
      </Row>
    </div>
  );
}

function ContactInfo({ handleUpdateDetails }) {
  const componentSize = useComponentSize();
  const { contact } = useStore();

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title
            level={componentSize === "default" ? 5 : 3}
            className={style.SectionTitle}
          >
            Contact Information
          </Title>
        </Col>
        <Col>
          <Button type="link" onClick={handleUpdateDetails}>
            Update Details
          </Button>
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Owner’s Legal First Name<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            size="large"
            variant="borderless"
            readOnly
            defaultValue={contact?.legalFirstName}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Owner’s Legal Last Name<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={contact?.legalLastName}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Owner’s Email Address<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={contact?.legalEmailAddress}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>
          Owner’s Phone Number<span style={{ color: "red" }}>*</span>
        </Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={contact?.legalPhoneNumber}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: "24px" }}>
        <Col>
          <Checkbox
            checked={contact?.agreeOwner}
            disabled
            className={`${style.checkboxAgree} BEVZ_Form`}
          >
            By signing up for texts, you agree to receive communications and
            messages about your service from Bevz. Message and data rates may
            apply. See our{" "}
            <a
              href="https://www.bevz.com/terms-of-service"
              target="_blank"
              className={style.link}
            >
              full terms
            </a>{" "}
            and{" "}
            <a
              href="https://www.bevz.com/privacy-policy"
              target="_blank"
              className={style.link}
            >
              privacy policy
            </a>
            .
          </Checkbox>
        </Col>
      </Row>

      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>Secondary Legal First Name</Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={contact?.secondaryFirstName || "No Input"}
            disabled={!contact?.secondaryFirstName}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>Secondary Legal Last Name</Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={contact?.secondaryLastName || "No Input"}
            disabled={!contact?.secondaryLastName}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>Secondary Email Address</Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={contact?.secondaryEmailAddress || "No Input"}
            disabled={!contact?.secondaryEmailAddress}
          />
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>Secondary Phone Number</Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={contact?.secondaryPhoneNumber || "No Input"}
            disabled={!contact?.secondaryPhoneNumber}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Checkbox
            checked={contact?.agreeSecondary}
            disabled
            className={`${style.checkboxAgree} BEVZ_Form`}
          >
            By signing up for texts, you agree to receive communications and
            messages about your service from Bevz. Message and data rates may
            apply. See our{" "}
            <a
              href="https://www.bevz.com/terms-of-service"
              target="_blank"
              className={style.link}
            >
              full terms
            </a>{" "}
            and{" "}
            <a
              href="https://www.bevz.com/privacy-policy"
              target="_blank"
              className={style.link}
            >
              privacy policy
            </a>
            .
          </Checkbox>
        </Col>
      </Row>
    </div>
  );
}

function BusinessInfo({ handleUpdateDetails }) {
  const componentSize = useComponentSize();
  const { business } = useStore();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordVisible2, setPasswordVisible2] = useState(false);

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title
            level={componentSize === "default" ? 5 : 3}
            className={style.SectionTitle}
          >
            Business Information
          </Title>
        </Col>
        <Col>
          <Button type="link" onClick={handleUpdateDetails}>
            Update Details
          </Button>
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>Business Type</Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={
              business?.typeOfEntity
                ? businessType.find(
                    (item) => item.value === business?.typeOfEntity,
                  )?.label
                : "No Input"
            }
            disabled={!business?.typeOfEntity}
          />
        </Col>
      </Row>
      {business?.typeOfEntity === businessType[4].value ? (
        <Row align="middle" className={style.input_wrapper}>
          <Col span={9}>Tax Classification</Col>
          <Col span={14} offset={1}>
            <Input
              variant="borderless"
              readOnly
              defaultValue={
                businessType.find(
                  (item) => item.value === business?.taxClassification,
                )?.label
              }
            />
          </Col>
        </Row>
      ) : (
        <Row align="middle" className={style.input_wrapper}>
          <Col span={9}>Last 4 Digits of Owner's SSN</Col>
          <Col span={14} offset={1}>
            {business?.ssn ? (
              <Input.Password
                variant="borderless"
                readOnly
                defaultValue={business?.ssn}
                style={{ width: "max-content" }}
                visibilityToggle={{
                  visible: passwordVisible,
                  onVisibleChange: setPasswordVisible,
                }}
              />
            ) : (
              <Input
                variant="borderless"
                readOnly
                defaultValue={"No Input"}
                style={{ width: "max-content" }}
                disabled={true}
              />
            )}
          </Col>
        </Row>
      )}

      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>Employer ID Number</Col>
        <Col span={14} offset={1}>
          {business?.ein ? (
            <Input.Password
              variant="borderless"
              readOnly
              defaultValue={business?.ein}
              style={{ width: "max-content" }}
              visibilityToggle={{
                visible: passwordVisible2,
                onVisibleChange: setPasswordVisible2,
              }}
            />
          ) : (
            <Input
              variant="borderless"
              readOnly
              defaultValue={"No Input"}
              style={{ width: "max-content" }}
              disabled={true}
            />
          )}
        </Col>
      </Row>
      <Row align="middle" className={style.input_wrapper}>
        <Col span={9}>Owner’s Date of Birth</Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={
              business?.legalDOB
                ? dayjs(business?.legalDOB).format("MMMM D, YYYY")
                : "No Input"
            }
            disabled={!business?.legalDOB}
          />
        </Col>
      </Row>
    </div>
  );
}

function StoreHours({ handleUpdateDetails }) {
  const componentSize = useComponentSize();
  const { operationHours } = useStore();

  const convertDaysToText = (days) => {
    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    return days
      .map((day) => daysOfWeek[day - 1])
      .join(", ")
      .replace(/,([^,]*)$/, " and$1");
  };

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title
            level={componentSize === "default" ? 5 : 3}
            className={style.SectionTitle}
          >
            Store Hours
          </Title>
        </Col>
        <Col>
          <Button type="link" onClick={handleUpdateDetails}>
            Update Details
          </Button>
        </Col>
      </Row>
      {operationHours.map((sched, index) => (
        <Row
          align="top"
          key={sched?.id || index}
          className={style.input_wrapper}
        >
          <Col span={9}>Schedule {sched?.id || index + 1}</Col>
          <Col span={14} offset={1}>
            <div
              style={{
                fontSize: "16px",
                color: "#515151",
                marginLeft: "11px",
              }}
            >
              {convertDaysToText(sched.days)}
            </div>
            <Input
              variant="borderless"
              readOnly
              defaultValue={`${sched.openTime} - ${sched.closeTime}`}
              style={{ paddingTop: 0 }}
            />
          </Col>
        </Row>
      ))}
    </div>
  );
}

function SubsPlan({ handleUpdateDetails }) {
  const componentSize = useComponentSize();
  const { plan: selectedPlan, integrator } = useStore();

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title
            level={componentSize === "default" ? 5 : 3}
            className={style.SectionTitle}
          >
            Subscription Plan
          </Title>
        </Col>
        <Col>
          <Button type="link" onClick={handleUpdateDetails}>
            Update Details
          </Button>
        </Col>
      </Row>
      <Row align="top">
        <Col span={9}>Selected Plan</Col>
        <Col span={14} offset={1}>
          <Input
            variant="borderless"
            readOnly
            defaultValue={`${plans[selectedPlan]?.label} Plan`}
            style={{ paddingBottom: 0 }}
          />
          {integrator?.id ? null : (
            <Input
              variant="borderless"
              readOnly
              defaultValue={`$${plans[selectedPlan]?.price}/month`}
              style={{ paddingTop: 0 }}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}
